import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react'
import { Check, ChevronDown, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

export type SearchOption = {
  id: string
  label: string
  /** A second, muted line (category, unit…). */
  detail?: string
  /** Heading of the block the option belongs to; options of one group must be consecutive. */
  group?: string | null
  /** The text the search matches (any case). */
  search: string
}

type Row = { key: string; id: string; label: string; detail?: string }
/** A block of the list: a group heading (or none) and its options. */
type Block = { key: string; label: string | null; rows: Row[] }

const noneRow = (label: string): Row => ({ key: 'none', id: '', label })

/**
 * The options rendered for a search: the first `limit` matches — plus, without a search, the
 * selected option even when it lies beyond the limit (so opening the list starts on it).
 */
function visibleOptions(
  options: readonly SearchOption[],
  needle: string,
  limit: number,
  selectedId: string | null,
): { shown: SearchOption[]; matchCount: number } {
  const matching = needle
    ? options.filter((option) => option.search.toLowerCase().includes(needle))
    : options
  const shown = matching.slice(0, limit)
  if (!needle && selectedId && !shown.some((option) => option.id === selectedId)) {
    const selected = matching.find((option) => option.id === selectedId)
    if (selected) shown.push(selected)
  }
  return { shown, matchCount: matching.length }
}

/**
 * A select with a search box at the top of its list, for long lists (categories, the cost-item
 * catalog). Options may be split into groups with headings. Keyboard: ↑/↓ move, Enter picks, Esc
 * closes (without closing the surrounding dialog), Tab moves on. No popover library: the list is an
 * absolutely positioned panel under the button.
 */
export function SearchableSelect({
  options,
  noneLabel,
  selectedId,
  buttonLabel,
  onSelect,
  labelledBy,
  ariaLabel,
  searchPlaceholder,
  listLabel,
  limit = 200,
}: {
  /** In display order. */
  options: readonly SearchOption[]
  /** When given, a first «none» option (id `''`), offered while searching too. */
  noneLabel?: string
  /** The selected id; `''` = none; `null` = a value the list does not know (legacy). */
  selectedId: string | null
  /** What the closed control shows. */
  buttonLabel: string
  onSelect: (id: string) => void
  labelledBy?: string
  /** Accessible name when there is no visible label to point at. */
  ariaLabel?: string
  searchPlaceholder: string
  listLabel: string
  /** At most this many matches are rendered (a big catalog stays fast); the rest need a search. */
  limit?: number
}) {
  const [isOpen, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const baseId = useId()
  const listId = `${baseId}-list`
  const none = noneLabel === undefined ? null : noneRow(noneLabel)

  const { blocks, matchCount } = useMemo(() => {
    const { shown, matchCount } = visibleOptions(options, query.trim().toLowerCase(), limit, selectedId)
    const showHeaders = shown.some((option) => option.group)
    const result: Block[] =
      noneLabel === undefined ? [] : [{ key: 'b:none', label: null, rows: [noneRow(noneLabel)] }]
    let current: Block | null = null
    for (const option of shown) {
      const group = option.group ?? null
      if (!current || (showHeaders && current.label !== group)) {
        current = { key: `g${result.length}:${group ?? ''}`, label: showHeaders ? group : null, rows: [] }
        result.push(current)
      }
      current.rows.push({ key: `o:${option.id}`, id: option.id, label: option.label, detail: option.detail })
    }
    return { blocks: result, matchCount }
  }, [options, query, limit, noneLabel, selectedId])
  const rows = blocks.flatMap((block) => block.rows)
  const hasMatches = matchCount > 0
  const activeIndex = Math.min(Math.max(active, 0), rows.length - 1)
  // A search that matches nothing highlights nothing (Enter does nothing; «none» stays clickable).
  const activeRow = !hasMatches && query.trim() !== '' ? null : (rows[activeIndex] ?? null)
  // Last pointer position over the list: Chrome fires a synthetic mousemove after scrollIntoView,
  // which must not steal the keyboard highlight — only a real pointer move does.
  const pointerRef = useRef({ x: -1, y: -1 })

  const close = (refocus: boolean) => {
    setOpen(false)
    if (refocus) buttonRef.current?.focus()
  }
  const pick = (id: string) => {
    onSelect(id)
    close(true)
  }
  const open = () => {
    setQuery('')
    // Start on the current selection (index in the unfiltered list; «none» is first).
    const all = [...(none ? [none] : []), ...visibleOptions(options, '', limit, selectedId).shown]
    setActive(Math.max(0, all.findIndex((item) => item.id === selectedId)))
    setOpen(true)
  }

  // While open: a click outside closes; Esc closes the list only — caught on `window` in the
  // capture phase, i.e. before Radix's Esc listener (document, capture phase) closes the dialog.
  useEffect(() => {
    if (!isOpen) return
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopPropagation()
      setOpen(false)
      buttonRef.current?.focus()
    }
    document.addEventListener('pointerdown', onPointerDown)
    window.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('keydown', onKeyDown, true)
    }
  }, [isOpen])

  // Keep the highlighted option visible.
  useEffect(() => {
    if (!isOpen || !activeRow) return
    listRef.current
      ?.querySelector(`[data-key="${CSS.escape(activeRow.key)}"]`)
      ?.scrollIntoView({ block: 'nearest' })
  }, [isOpen, activeRow])

  const onSearchKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActive(Math.min(activeIndex + 1, rows.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActive(Math.max(activeIndex - 1, 0))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      // With a search that matches nothing, Enter must not silently pick «none».
      if (activeRow) pick(activeRow.id)
    }
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        className={cn(
          'border-input bg-background flex h-9 w-full min-w-0 cursor-pointer items-center justify-between gap-2 rounded-md border px-3 py-1 text-left text-sm shadow-xs outline-none',
          'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
        )}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-labelledby={labelledBy}
        aria-label={labelledBy || !ariaLabel ? undefined : `${ariaLabel}: ${buttonLabel}`}
        // While open, keep the focus in the search box: otherwise its blur closes the list and this
        // click would reopen it.
        onMouseDown={(event) => {
          if (isOpen) event.preventDefault()
        }}
        onClick={() => (isOpen ? close(true) : open())}
        onKeyDown={(event) => {
          if (!isOpen && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
            event.preventDefault()
            open()
          }
        }}
      >
        <span className="truncate" title={buttonLabel}>
          {buttonLabel}
        </span>
        <ChevronDown className="text-muted-foreground size-4 shrink-0" />
      </button>

      {isOpen ? (
        <div
          className="bg-popover text-popover-foreground absolute z-50 mt-1 w-full min-w-64 rounded-md border shadow-md"
          // Clicks on headings, padding or the scrollbar must not blur the search box (that closes the
          // list); the search box itself keeps normal clicks for the caret.
          onMouseDown={(event) => {
            if (!(event.target instanceof HTMLInputElement)) event.preventDefault()
          }}
        >
          <div className="relative border-b p-2">
            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-4 size-4 -translate-y-1/2" />
            <Input
              value={query}
              onChange={(event) => {
                setQuery(event.target.value)
                // The first match, not «none».
                setActive(event.target.value.trim() && none ? 1 : 0)
              }}
              onKeyDown={onSearchKeyDown}
              // Tab (or any focus move) closes; option clicks keep the focus here (mousedown above).
              onBlur={() => setOpen(false)}
              placeholder={searchPlaceholder}
              className="pl-8"
              autoFocus
              role="combobox"
              aria-expanded
              aria-controls={listId}
              aria-activedescendant={activeRow ? `${baseId}-${activeRow.key}` : undefined}
              aria-label={searchPlaceholder}
            />
          </div>
          <div
            ref={listRef}
            id={listId}
            role="listbox"
            aria-label={listLabel}
            className="max-h-64 overflow-y-auto p-1"
          >
            {blocks.map((block) => (
              <div
                key={block.key}
                role="group"
                aria-labelledby={block.label ? `${baseId}-${block.key}` : undefined}
              >
                {block.label ? (
                  <div
                    id={`${baseId}-${block.key}`}
                    className="text-muted-foreground px-2 pt-2 pb-1 text-xs font-medium"
                  >
                    {block.label}
                  </div>
                ) : null}
                {block.rows.map((row) => (
                  <div
                    key={row.key}
                    id={`${baseId}-${row.key}`}
                    data-key={row.key}
                    role="option"
                    aria-selected={row.id === selectedId}
                    className={cn(
                      'flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm',
                      row === activeRow ? 'bg-accent text-accent-foreground' : null,
                      row.id === '' ? 'text-muted-foreground' : null,
                    )}
                    onMouseMove={(event) => {
                      const last = pointerRef.current
                      if (last.x === event.clientX && last.y === event.clientY) return
                      pointerRef.current = { x: event.clientX, y: event.clientY }
                      setActive(rows.indexOf(row))
                    }}
                    onClick={() => pick(row.id)}
                  >
                    <Check
                      className={cn('size-4 shrink-0', row.id === selectedId ? 'opacity-100' : 'opacity-0')}
                    />
                    <span className="grid min-w-0">
                      <span className="truncate">{row.label}</span>
                      {row.detail ? (
                        <span className="text-muted-foreground truncate text-xs">{row.detail}</span>
                      ) : null}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
          {!hasMatches && query.trim() ? (
            <p className="text-muted-foreground border-t px-2 py-3 text-center text-sm">
              Ничего не найдено
            </p>
          ) : matchCount > limit ? (
            <p className="text-muted-foreground border-t px-2 py-2 text-center text-xs">
              Показаны первые {limit} из {matchCount} — уточните поиск
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
